import React from 'react';
import moment from 'moment';
import {FiBookOpen, FiUsers, FiUser, FiCalendar, FiClock, FiMapPin} from 'react-icons/fi';
import {bookingLocationLabel} from '../../utility/bookingLocation';
import './styles.css';

export const locationNotes = location =>
  String(location?.notes || '')
    .replace(/<\/(?:p|div)>|<br\s*\/?>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .trim();

export function bookingMemberNames(booking = {}, member = {}) {
  const name = person =>
    person?.name || [person?.firstName, person?.lastName].filter(Boolean).join(' ');
  return booking?.familyMembers?.length
    ? booking.familyMembers
        .map(person => name(person.isParent ? member : person))
        .filter(Boolean)
        .join(', ') || 'None'
    : [booking?.memberFirstName, booking?.memberLastName].filter(Boolean).join(' ') ||
        (booking?.isBooked ? name(member) : '') ||
        'None';
}

export function BookingLocation({booking = {}, showMap = false, showMeetingLink = false, fallback}) {
  const location = booking?.savedLocation;
  const label = bookingLocationLabel(booking, {showMeetingLink}) || fallback || 'Not specified';
  const address = location?.type === 'address' ? location.address : '';
  const url = location?.type === 'virtual' ? location.url : !location ? booking?.zoomLink : '';
  const safeUrl = /^https?:\/\//i.test(url || '') ? url : '';
  const notes = location?.type !== 'other' ? locationNotes(location) : '';
  return (
    <div className="booking-location">
      <span className="booking-location__label">{label}</span>
      {notes && <span className="booking-location__notes">{notes}</span>}
      {showMeetingLink && safeUrl && (
        <a href={safeUrl} target="_blank" rel="noreferrer">
          Join online session
        </a>
      )}
      {showMap && address && (
        <div className="booking-location__map">
          <iframe
            title={`Map of ${location.name || address}`}
            loading="lazy"
            src={`https://maps.google.com/maps?q=${encodeURIComponent(address)}&output=embed`}
            referrerPolicy="no-referrer-when-downgrade"
          />
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
            target="_blank"
            rel="noreferrer"
          >
            View map and directions
          </a>
        </div>
      )}
    </div>
  );
}

export function BookingDetailRow({label, icon: Icon, children}) {
  return (
    <li className="booking-details__row">
      <span className="booking-details__icon" aria-hidden="true">
        <Icon />
      </span>
      <div className="booking-details__content">
        <strong>{label}: </strong>
        {children || 'None'}
      </div>
    </li>
  );
}

export default function BookingDetails({
  service,
  members,
  memberLabel = 'Members Booked',
  coach,
  start,
  end,
  date,
  time,
  booking,
  showMap = false,
  showMeetingLink = false,
  location,
  coachFirst = false,
}) {
  const validStart = start && moment(start).isValid();
  const validEnd = end && moment(end).isValid();
  const memberRow = (
    <BookingDetailRow key="members" label={memberLabel} icon={FiUsers}>
      {members}
    </BookingDetailRow>
  );
  const coachRow = (
    <BookingDetailRow key="coach" label="Coach" icon={FiUser}>
      {coach}
    </BookingDetailRow>
  );
  return (
    <ul className="booking-details" aria-label="Booking details">
      <BookingDetailRow label="Service" icon={FiBookOpen}>
        {service}
      </BookingDetailRow>
      {coachFirst ? [coachRow, memberRow] : [memberRow, coachRow]}
      <BookingDetailRow label="Date" icon={FiCalendar}>
        {date ||
          (validStart ? moment.parseZone(start).format('dddd, MMMM Do YYYY') : 'Not specified')}
      </BookingDetailRow>
      <BookingDetailRow label="Time" icon={FiClock}>
        {time ||
          (validStart
            ? `${moment.parseZone(start).format('h:mm A')}${
                validEnd ? ` – ${moment.parseZone(end).format('h:mm A')}` : ''
              }`
            : 'Not specified')}
      </BookingDetailRow>
      <BookingDetailRow label="Location" icon={FiMapPin}>
        {location || (
          <BookingLocation booking={booking} showMap={showMap} showMeetingLink={showMeetingLink} />
        )}
      </BookingDetailRow>
    </ul>
  );
}
